import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const LoanArrears = () => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nicFilter, setNicFilter] = useState('');
  const [totalArrears, setTotalArrears] = useState(0);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);

  useEffect(() => {
    fetchArrears();
  }, []);

  useEffect(() => {
    // Debounce NIC filter
    const timer = setTimeout(() => {
      fetchArrears();
    }, 300);

    return () => clearTimeout(timer);
  }, [nicFilter]);

  const fetchArrears = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (nicFilter && nicFilter.trim()) {
        params.append('nic', nicFilter.trim());
      }

      const response = await api.get(`/admin/loans/arrears?${params.toString()}`);
      setLoans(response.data.loans || []);
      setTotalArrears(response.data.totalArrears || 0);
    } catch (err) {
      console.error('Error fetching loan arrears:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load arrears');
      setLoans([]);
      setTotalArrears(0);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Rs 0.00';
    return `Rs ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    return new Date(dateTime).toLocaleString();
  };

  const handleViewCustomer = (loan) => {
    setSelectedLoan(loan);
    setCustomerDialogOpen(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Loan Arrears (Admin-Closed Loans)
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={fetchArrears}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        This page shows all loans closed by admin that have outstanding arrears. 
        These amounts are tracked separately and are NOT deducted from system capital.
      </Alert>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search by NIC"
              value={nicFilter}
              onChange={(e) => setNicFilter(e.target.value)}
              placeholder="Enter customer NIC to search"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={fetchArrears}
              disabled={loading}
              fullWidth
              sx={{ height: '56px' }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card
            elevation={3}
            sx={{
              p: 2,
              backgroundColor: 'error.main',
              color: 'white',
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              Total Arrears
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {formatCurrency(totalArrears)}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
              Outstanding amount
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card
            elevation={3}
            sx={{
              p: 2,
              backgroundColor: 'warning.main',
              color: 'white',
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              Total Loans
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {loans.length}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
              Loans with arrears
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>#</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Loan ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Customer Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>NIC</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Loan Amount</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Arrears Amount</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Closed By</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Closed Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <CircularProgress sx={{ mr: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Loading arrears...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : loans.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {nicFilter ? 'No arrears found for the specified NIC.' : 'No loans with arrears found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              loans.map((loan, index) => (
                <TableRow key={loan.id} hover>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{loan.id}</TableCell>
                  <TableCell>{loan.customer?.fullName || 'N/A'}</TableCell>
                  <TableCell>{loan.customer?.nic || 'N/A'}</TableCell>
                  <TableCell>{formatCurrency(loan.approvedAmount || loan.requestedAmount)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={formatCurrency(loan.arrearsAmount)} 
                      color="error" 
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell>{loan.closedByAdminUser?.fullName || 'Admin'}</TableCell>
                  <TableCell>{formatDateTime(loan.closedAt)}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PersonIcon />}
                      onClick={() => handleViewCustomer(loan)}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Customer Details Dialog */}
      <Dialog 
        open={customerDialogOpen} 
        onClose={() => setCustomerDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Customer & Loan Details - Loan #{selectedLoan?.id}
        </DialogTitle>
        <DialogContent>
          {selectedLoan && selectedLoan.customer && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                Customer Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Full Name:</strong> {selectedLoan.customer.fullName || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>NIC:</strong> {selectedLoan.customer.nic || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Phone:</strong> {selectedLoan.customer.phoneNumber || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Address:</strong> {selectedLoan.customer.address || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>District:</strong> {selectedLoan.customer.district || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Business Address:</strong> {selectedLoan.customer.businessAddress || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Occupation:</strong> {selectedLoan.customer.occupation || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Spouse Name:</strong> {selectedLoan.customer.spouseFullName || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Spouse NIC:</strong> {selectedLoan.customer.spouseNic || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Spouse Phone:</strong> {selectedLoan.customer.spousePhoneNumber || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Bank Name:</strong> {selectedLoan.customer.bankName || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Bank Branch:</strong> {selectedLoan.customer.bankBranch || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Account Number:</strong> {selectedLoan.customer.bankAccountNumber || 'N/A'}</Typography>
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Loan Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Loan ID:</strong> {selectedLoan.id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Loan Amount:</strong> {formatCurrency(selectedLoan.approvedAmount || selectedLoan.requestedAmount)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Arrears Amount:</strong>{' '}
                    <Chip 
                      label={formatCurrency(selectedLoan.arrearsAmount)} 
                      color="error" 
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Closed By:</strong> {selectedLoan.closedByAdminUser?.fullName || 'Admin'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Closed Date:</strong> {formatDateTime(selectedLoan.closedAt)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Disbursement Date:</strong> {selectedLoan.disbursementDate ? new Date(selectedLoan.disbursementDate).toLocaleDateString() : 'N/A'}</Typography>
                </Grid>
                {selectedLoan.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2"><strong>Notes:</strong></Typography>
                    <Typography variant="body2" sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1, whiteSpace: 'pre-wrap' }}>
                      {selectedLoan.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomerDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoanArrears;
